import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { consultantsService, ConsultantService } from '../../services/consultants.service';
import { trainingsService } from '../../services/trainings.service';
import { API_ENDPOINTS } from '../../config/api';
import { 
  Download, 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  Briefcase, 
  Edit, 
  ArrowLeft,
  Eye,
  MessageSquare,
  FileText,
  Globe,
  Phone,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

const ViewServicePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [service, setService] = useState<ConsultantService | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadService();
    }
  }, [id]);

  const loadService = async () => {
    try {
      setIsLoading(true);
      const data = await consultantsService.getService(id!);
     
      setService(data);
      
      // Track view - services are stored in trainings table with service_type = 'consulting_service'
      // So we can use the same tracking method as trainings
      try {
        await trainingsService.trackTrainingView(id!);
      } catch (viewError) {
        // Don't fail the entire load if view tracking fails
      }
    } catch (error) {
      setError(t('consultant.view.errors.load', 'Unable to load service details'));
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          label: t('common.status.approved', 'Approved'),
          color: 'text-green-600 bg-green-50',
          icon: CheckCircle,
          iconColor: 'text-green-600'
        };
      case 'pending':
        return {
          label: t('common.status.pending', 'Pending'),
          color: 'text-yellow-600 bg-yellow-50',
          icon: AlertCircle,
          iconColor: 'text-yellow-600'
        };
      case 'refused':
        return {
          label: t('common.status.rejected', 'Rejected'),
          color: 'text-red-600 bg-red-50',
          icon: XCircle,
          iconColor: 'text-red-600'
        };
      default:
        return {
          label: t('common.status.unknown', 'Unknown'),
          color: 'text-gray-600 bg-gray-50',
          icon: AlertCircle,
          iconColor: 'text-gray-600'
        };
    }
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case 'presentiel':
        return t('consultant.create.form.format.presentiel', 'In-person');
      case 'distance':
        return t('consultant.create.form.format.distance', 'Remote');
      case 'hybride':
        return t('consultant.create.form.format.hybride', 'Hybrid');
      default:
        return format;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('consultant.view.notFound.title', 'Service not found')}</h2>
          <p className="text-gray-600 mb-4">{error || t('consultant.view.notFound.subtitle', 'The service you are looking for does not exist.')}</p>
          <button
            onClick={() => navigate('/consultant/services')}
            className="bg-brand-primary text-white px-4 py-2 rounded-md hover:bg-brand-primary/90"
          >
            {t('consultant.common.backToServices', 'Back to services')}
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(service.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/consultant/services')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{service.title}</h1>
                <p className="text-gray-600">{t('consultant.view.header.subtitle', 'Service details')}</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate(`/consultant/services/${service.id}/edit`)}
                className="bg-brand-primary text-white px-4 py-2 rounded-md hover:bg-brand-primary/90 flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                {t('consultant.view.actions.edit', 'Edit service')}
              </button>
              <button
                onClick={() => navigate('/consultant/services')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('consultant.common.backToServices', 'Back to services')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Banner Image with Download Button */}
        {service.banner_image && (
          <div className="relative mb-8">
            <img
              src={`${API_ENDPOINTS.UPLOADS}/${service.banner_image}`}
              alt={service.title}
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
            <div className="absolute top-4 right-4">
              <button className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-2 rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                <Download className="h-4 w-4" />
                {t('consultant.view.banner.download', 'Download')}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Service Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('consultant.view.overview.title', 'Service overview')}</h2>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                      <statusInfo.icon className={`h-4 w-4 inline mr-1 ${statusInfo.iconColor}`} />
                      {statusInfo.label}
                    </span>
                    <span className="text-sm text-gray-500">
                      {service.views} {t('consultant.view.overview.views', 'views')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.overview.reference', 'Reference')}</p>
                    <p className="font-medium text-gray-900">{service.reference}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.overview.format', 'Format')}</p>
                    <p className="font-medium text-gray-900">{getFormatLabel(service.format)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.overview.duration', 'Estimated duration')}</p>
                    <p className="font-medium text-gray-900">{(service as any).duration || t('consultant.view.overview.notSpecified', 'Not specified')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Globe className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.overview.languages', 'Languages')}</p>
                    <p className="font-medium text-gray-900">{service.language || t('consultant.view.overview.notSpecifiedPlural', 'Not specified')}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('consultant.view.description.title', 'Full description')}</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {service.full_description}
                </p>
              </div>
            </div>

            {/* Skills and Objectives */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('consultant.view.skillsObjectives.title', 'Skills and objectives')}</h2>
              
              <div className="space-y-6">
                {service.skills_gained && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.view.skillsObjectives.skills', 'Skills applied')}</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {service.skills_gained}
                    </p>
                  </div>
                )}

                {service.learning_objectives && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.view.skillsObjectives.objectives', 'Service objectives')}</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {service.learning_objectives}
                    </p>
                  </div>
                )}

                {service.target_audience && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.view.skillsObjectives.targets', 'Target companies / Audience')}</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {service.target_audience}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Consultant Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('consultant.view.consultantInfo.title', 'Consultant information')}</h2>
              
              <div className="space-y-4">
                {service.consultant_names && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.view.consultantInfo.consultants', 'Consultants')}</h3>
                    <p className="text-gray-700">{service.consultant_names}</p>
                  </div>
                )}

                {service.availability_calendar && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('consultant.view.consultantInfo.availabilities', 'One-to-one availabilities')}</h3>
                    <p className="text-gray-700">{service.availability_calendar}</p>
                  </div>
                )}
              </div>
            </div>

            {/* PDF Document */}
            {service.detailed_program_pdf && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('consultant.view.pdf.title', 'Presentation document')}</h2>
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FileText className="h-8 w-8 text-gray-400" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{t('consultant.view.pdf.subtitle', 'Presentation PDF')}</p>
                    <p className="text-sm text-gray-600">{t('consultant.view.pdf.description', 'Detailed service document')}</p>
                  </div>
                  <button className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-primary/90 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    {t('consultant.view.banner.download', 'Download')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Service Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('consultant.view.sidebar.title', 'Service details')}</h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.sidebar.location', 'Location')}</p>
                    <p className="font-medium text-gray-900">
                      {service.wilaya?.name}, {typeof service.country === 'string' ? service.country : service.country?.name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Briefcase className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.sidebar.domain', 'Domain of expertise')}</p>
                    <p className="font-medium text-gray-900">{service.domaine?.name_french}</p>
                  </div>
                </div>

                {service.subcategory && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">{t('consultant.view.sidebar.subcategory', 'Subcategory')}</p>
                      <p className="font-medium text-gray-900">{service.subcategory.name_french}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.sidebar.createdAt', 'Created on')}</p>
                    <p className="font-medium text-gray-900">{formatDate(service.created_at)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Eye className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">{t('consultant.view.sidebar.views', 'Views')}</p>
                    <p className="font-medium text-gray-900">{service.views}</p>
                  </div>
                </div>
              </div>
            </div>


            {/* Status Information */}
            {service.status === 'refused' && service.rejection_reason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                  <h4 className="text-sm font-medium text-red-800">{t('consultant.view.rejection.title', 'Rejection reason')}</h4>
                    <p className="text-sm text-red-700 mt-1">{service.rejection_reason}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewServicePage;
